import { useT } from '@transifex/react';
import { Avatar, AvatarImage, Checkbox, Text, VStack } from '@vidahealth/vida-ui';

import { CheckboxCheckIndicator } from '#modules/ui/CheckboxCheckIndicator';
import { SelectableRow } from '#modules/ui/SelectableRow';
import { TextField } from '#modules/ui/TextField';

import { OTHER_CHOICE_VALUE } from '../../../../types/assessmentQuestion';
import { slugify } from '../../../../utils/questionFlags';
import type { ActiveQuestion } from '../../../../utils/resolveQuestionChoices';

export type ChoiceFieldProps = {
  question: ActiveQuestion;
  selected: string[];
  isMultiSelect: boolean;
  /** The question opted into SurveyJS's `showOtherItem` inline text field. */
  hasOtherItem: boolean;
  otherInput: string;
  onToggleChoice: (value: string) => void;
  onOtherInputChange: (text: string) => void;
  testIDPrefix?: string;
};

/**
 * A choice question's answer rows — radio (single-select) or checkbox
 * (multi-select), with the inline Other field revealed under the Other row
 * when it's checked. A choice's own `description` (when authored) renders as
 * a secondary line under its row, but ONLY while that choice is selected —
 * unlike the question-level `additionalInfo`, which is permanently visible.
 * When a choice is both selected and the Other row, the description renders
 * above the inline TextField, not below it.
 *
 * The description sits OUTSIDE `SelectableRow` (mirroring its `px-6` to stay
 * aligned) rather than nested inside it. Nesting was tried and reverted: the
 * row is a `Pressable`, which defaults to `accessible={true}`, so everything
 * inside collapses into one a11y node whose explicit `accessibilityLabel`
 * hides the description from screen readers entirely — defeating the
 * `accessibilityLiveRegion` below. Nesting also puts the description in the
 * row's tap target, where the only reachable outcome is DEselecting the
 * choice (the description only exists once selected), so a member tapping
 * the copy would silently clear their answer.
 */
export function ChoiceField({
  question,
  selected,
  isMultiSelect,
  hasOtherItem,
  otherInput,
  onToggleChoice,
  onOtherInputChange,
  testIDPrefix,
}: ChoiceFieldProps) {
  const t = useT();

  return (
    <VStack space='lg'>
      {question.choices.map((choice) => {
        const isSelected = selected.includes(choice.value);
        const showOtherField = hasOtherItem && choice.value === OTHER_CHOICE_VALUE && isSelected;
        return (
          <VStack key={choice.value} space='sm'>
            <SelectableRow
              isSelected={isSelected}
              selectionRole={isMultiSelect ? 'checkbox' : 'radio'}
              onPress={() => onToggleChoice(choice.value)}
              accessibilityLabel={choice.text}
              // Checkbox rows (label-only) get the taller HealthConditions
              // padding; avatar rows already grow to fit the `lg` icon, so
              // extra padding there would make them too tall.
              className={isMultiSelect ? 'py-6' : undefined}
              testID={testIDPrefix ? `${testIDPrefix}-${slugify(choice.text)}` : undefined}
            >
              {isMultiSelect ? (
                <Checkbox value={choice.value} isChecked={isSelected} pointerEvents='none'>
                  <CheckboxCheckIndicator />
                </Checkbox>
              ) : null}
              {choice.imageLink ? (
                <Avatar size='lg' className='bg-[#FDF6EE]'>
                  <AvatarImage source={{ uri: choice.imageLink }} alt={choice.text} />
                </Avatar>
              ) : null}
              <Text size='md' weight='semibold' className='flex-1 text-typography-900'>
                {choice.text}
              </Text>
            </SelectableRow>
            {isSelected && choice.description ? (
              <Text
                size='sm'
                // Matches SelectableRow's own `px-6` so the description lines
                // up with the row's content instead of the container edge.
                className='px-6 text-typography-600'
                accessibilityLiveRegion='polite'
                testID={
                  testIDPrefix ? `${testIDPrefix}-${slugify(choice.text)}-description` : undefined
                }
              >
                {choice.description}
              </Text>
            ) : null}
            {showOtherField ? (
              <TextField
                value={otherInput}
                onChangeText={onOtherInputChange}
                placeholder={question.otherPlaceholder ?? t('Please specify')}
                testID={testIDPrefix ? `${testIDPrefix}-other-text` : undefined}
              />
            ) : null}
          </VStack>
        );
      })}
    </VStack>
  );
}
